import { createApi } from '@reduxjs/toolkit/query/react';
import { appEndpoint, releaseEndpoint } from '@/helpers/apiHelpers';
import type { AppHome } from '@/types';
import { type BaseAPIResponse, baseQuery } from './common';
import qs from 'query-string';
import { GetPendingAssociationTicketRequest, GetPendingAssociationTicketResponse } from '@/actions/releaseStep';

type BaseRequest = {
    orgId: string;
    appId: string;
};

export const defaultApi = createApi({
    reducerPath: 'defaultApi',
    baseQuery: baseQuery,
    tagTypes: ['AppHome', 'PendingAssociationTickets'],
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        getAppHome: build.query<BaseAPIResponse<AppHome>, BaseRequest>({
            query: ({ orgId, appId }) => {
                const appUrl = appEndpoint(orgId, appId);
                return `${appUrl}/appHome`;
            },
            providesTags: (_result, _error, { orgId, appId }) => [
                {
                    type: 'AppHome',
                    id: `${orgId}-${appId}`,
                },
            ],
        }),
        getPendingAssociationTickets: build.query<
            GetPendingAssociationTicketResponse,
            GetPendingAssociationTicketRequest
        >({
            query: ({ orgId, appId, releaseId, searchCriteria }) => {
                const encodedQueryParams = qs.stringify(
                    {
                        searchCriteria,
                    },
                    { skipNull: true, skipEmptyString: true },
                );

                const releaseUrl = releaseEndpoint(orgId, appId, releaseId);
                return `${releaseUrl}/ticketsPendingAssociation?${encodedQueryParams}`;
            },
            providesTags: (_result, _error, { orgId, appId, releaseId }) => [
                {
                    type: 'PendingAssociationTickets',
                    id: `${orgId}-${appId}-${releaseId}`,
                },
            ],
        }),
    }),
});

export const invalidateAppHomeCache = (args: { orgId: string; appId: string }) => {
    console.log(`Invalid app home cache for ${args.appId}`);
    return defaultApi.util.invalidateTags([{ type: 'AppHome', id: `${args.orgId}-${args.appId}` }]);
};

export const { useGetAppHomeQuery, useGetPendingAssociationTicketsQuery } = defaultApi;
